import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './ProcessParametersSection.css';

const ProcessParametersSection = ({ 
  processParameters, 
  selectedProcessParameters, 
  onParameterChange, 
  onParameterTypeUpdate,
  readOnlyMode = false,
  validationErrors = {} 
}) => {
  const [inputParameters, setInputParameters] = useState([]);
  const [outputParameters, setOutputParameters] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  // Split parameters by type whenever processParameters changes
  useEffect(() => {
    console.log('ProcessParametersSection: processParameters changed', {
      totalCount: processParameters.length,
      sampleParameter: processParameters[0] ? {
        id: processParameters[0].id,
        name: processParameters[0].name,
        processParameterType: processParameters[0].processParameterType
      } : null
    });
    
    const input = processParameters.filter(p => p.processParameterType === 'Input Process Parameter');
    const output = processParameters.filter(p => p.processParameterType === 'Output Process Parameter');
    
    console.log('ProcessParametersSection: Parameters split', {
      inputCount: input.length,
      outputCount: output.length,
      inputSample: input.slice(0, 2).map(p => p.name),
      outputSample: output.slice(0, 2).map(p => p.name)
    });
    
    setInputParameters(input);
    setOutputParameters(output);
  }, [processParameters]);

  const handleDragStart = (e, parameter, sourceType) => {
    if (readOnlyMode) {
      e.preventDefault();
      return;
    }
    
    console.log('ProcessParametersSection: handleDragStart', {
      parameterId: parameter.id,
      parameterName: parameter.name,
      sourceType: sourceType
    });
    
    setDraggedItem({ parameter, sourceType });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      parameterId: parameter.id,
      sourceType: sourceType
    }));
  };

  const handleDragOver = (e) => {
    if (readOnlyMode || !draggedItem) return;
    
    console.log('ProcessParametersSection: handleDragOver triggered', {
      draggedItem: draggedItem,
      targetElement: e.currentTarget,
      readOnlyMode: readOnlyMode
    });
    
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, targetType) => {
    if (readOnlyMode || !draggedItem) return;
    
    console.log('ProcessParametersSection: handleDragEnter', {
      targetType: targetType,
      draggedItemSourceType: draggedItem.sourceType,
      isDifferentZone: draggedItem.sourceType !== targetType
    });
    
    e.preventDefault();
    e.stopPropagation();
    
    // Only highlight if this is a different zone than the source
    if (draggedItem.sourceType !== targetType) {
      setDragOverTarget(targetType);
    }
  };

  const handleDragLeave = (e) => {
    if (readOnlyMode || !draggedItem) return;
    
    console.log('ProcessParametersSection: handleDragLeave', {
      currentTarget: e.currentTarget,
      relatedTarget: e.relatedTarget
    });
    
    e.preventDefault();
    e.stopPropagation();
    
    // Only clear highlight if we're really leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget(null);
    }
  };

  const handleDrop = async (e, targetType) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('ProcessParametersSection: handleDrop triggered', {
      draggedItem: draggedItem,
      targetType: targetType,
      readOnlyMode: readOnlyMode
    });
    
    if (!draggedItem || readOnlyMode) {
      console.log('ProcessParametersSection: Aborting drop - no dragged item or read-only mode');
      setDraggedItem(null);
      return;
    }
    
    const { parameter, sourceType } = draggedItem;
    
    // Don't do anything if dropped in the same list
    if (sourceType === targetType) {
      console.log('ProcessParametersSection: Dropped in same list, ignoring');
      setDraggedItem(null);
      return;
    }
    
    // Debug logging
    console.log('ProcessParametersSection: Processing drop', {
      parameter: parameter,
      sourceType: sourceType,
      targetType: targetType,
      parameterId: parameter.id,
      parameterName: parameter.name
    });
    
    // Check if parameter has ID
    if (!parameter.id) {
      console.error('ProcessParametersSection: Parameter missing ID field', parameter);
      alert('Error: Parameter is missing ID field and cannot be updated');
      setDraggedItem(null);
      return;
    }
    
    // Convert target type to the simplified format expected by API
    const apiTargetType = targetType === "Input Process Parameter" ? "Input" : "Output";
    
    console.log('ProcessParametersSection: Converting type', {
      targetType: targetType,
      apiTargetType: apiTargetType
    });
    
    // Store original state for potential rollback
    const originalInputParams = [...inputParameters];
    const originalOutputParams = [...outputParameters];
    
    // Create updated parameter object
    const updatedParameter = {
      ...parameter,
      processParameterType: targetType
    };
    
    // Optimistic UI update - immediately update local state
    try {
      if (sourceType === "Input Process Parameter") {
        setInputParameters(prev => prev.filter(p => p.id !== parameter.id));
        setOutputParameters(prev => [...prev, updatedParameter]);
      } else {
        setOutputParameters(prev => prev.filter(p => p.id !== parameter.id));
        setInputParameters(prev => [...prev, updatedParameter]);
      }
      
      console.log('ProcessParametersSection: Optimistic UI update completed');
      
      // Call the parent component's update function
      if (onParameterTypeUpdate) {
        console.log('ProcessParametersSection: Calling onParameterTypeUpdate');
        await onParameterTypeUpdate(parameter.id, apiTargetType);
        console.log('ProcessParametersSection: onParameterTypeUpdate completed successfully');
      } else {
        throw new Error('onParameterTypeUpdate function not provided');
      }
      
    } catch (error) {
      console.error('ProcessParametersSection: Error updating parameter:', error);
      
      // Revert to original state on error
      setInputParameters(originalInputParams);
      setOutputParameters(originalOutputParams);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Failed to update parameter type: ${errorMessage}`);
    }
    
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  const ParameterCard = ({ parameter, sourceType }) => (
    <div
      className={`parameter-card ${draggedItem?.parameter.id === parameter.id ? 'dragging' : ''}`}
      draggable={!readOnlyMode}
      onDragStart={(e) => handleDragStart(e, parameter, sourceType)}
      onDragEnd={handleDragEnd}
      style={{ cursor: readOnlyMode ? 'default' : 'grab' }}
    >
      <div className="parameter-card-content">
        <div className="parameter-main-info">
          <Form.Check
            type="checkbox"
            label={parameter.name}
            id={`param-${parameter.id || parameter.name.replace(/\s+/g, '_')}`}
            checked={selectedProcessParameters[parameter.name] || false}
            onChange={(e) => onParameterChange(parameter.name, e.target.checked)}
            disabled={readOnlyMode}
            title={parameter.description || parameter.name}
            className="parameter-checkbox"
          />
          <span className={`parameter-tag ${parameter.processParameterType === 'Input Process Parameter' ? 'tag-input' : 'tag-output'}`}>
            {parameter.processParameterType === 'Input Process Parameter' ? 'Input' : 'Output'}
          </span>
        </div>
        {/* Removed parameter.unit and parameter.type display as requested */}
      </div>
      {!readOnlyMode && (
        <div className="drag-handle" title="Drag to move between categories">
          ⋮⋮
        </div>
      )}
    </div>
  );

  const ParameterList = ({ title, parameters, type, icon }) => (
    <div
      className={`parameter-list ${
        draggedItem && draggedItem.sourceType !== type ? 'drop-zone-active' : ''
      } ${
        dragOverTarget === type ? 'drop-zone-highlight' : ''
      }`}
      onDrop={(e) => handleDrop(e, type)}
      onDragOver={handleDragOver}
      onDragEnter={(e) => handleDragEnter(e, type)}
      onDragLeave={handleDragLeave}
    >
      <div className="parameter-list-header">
        <h6>
          <span className="parameter-icon">{icon}</span>
          {title}
        </h6>
        <span className="parameter-count">{parameters.length}</span>
      </div>
      
      <div className="parameter-list-content">
        {parameters.length === 0 ? (
          <div className="empty-drop-zone">
            <p>No {title.toLowerCase()}</p>
            {!readOnlyMode && <small>Drag parameters here to categorize them</small>}
          </div>
        ) : (
          parameters.map((parameter) => (
            <ParameterCard
              key={parameter.id || parameter.name}
              parameter={parameter}
              sourceType={type}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="process-parameters-section">
      <div className="section-header">
        <h5>Process Parameters</h5>
        {!readOnlyMode && (
          <small className="drag-instructions">
            Drag parameters between categories to organize them
          </small>
        )}
      </div>
      
      {validationErrors.processParameters && (
        <div className="validation-error mb-3">
          {validationErrors.processParameters}
        </div>
      )}

      <div className="parameters-grid">
        <ParameterList
          title="Input Process Parameters"
          parameters={inputParameters}
          type="Input Process Parameter"
          icon="📥"
        />
        
        <ParameterList
          title="Output Process Parameters"
          parameters={outputParameters}
          type="Output Process Parameter"
          icon="📤"
        />
      </div>
      
      {processParameters.length === 0 && (
        <div className="no-parameters-message">
          <p>No process parameters available</p>
          <small>Parameters will be loaded from the database</small>
        </div>
      )}
    </div>
  );
};

export default ProcessParametersSection;
